/**
 * Modal Component
 * OmniMind sheet: ink scrim, white panel, 24px corners.
 */

import React, { useEffect } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children: React.ReactNode;
  footer?: React.ReactNode;
  closeOnBackdrop?: boolean;
}

const widths: Record<string, number> = {
  sm: 440,
  md: 640,
  lg: 860,
  xl: 1020,
  full: 1200,
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  closeOnBackdrop = true,
}) => {
  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 95,
        display: 'grid',
        placeItems: 'center',
        padding: 16,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        className="ovl"
        style={{ zIndex: 0 }}
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      <div
        className="sheet animate-scale-in"
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: widths[size],
          maxHeight: '90vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="sheet-h">
            <h4 id="modal-title" style={{ flex: 1 }}>
              {title}
            </h4>
            <button className="icb" onClick={onClose} aria-label="Close modal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <div className="sheet-b custom-scrollbar">{children}</div>

        {footer && <div className="sheet-f">{footer}</div>}
      </div>
    </div>
  );
};
